import React, { useState, useEffect } from 'react'
import * as ReactHelmetAsync from 'react-helmet-async'
const { Helmet } = ReactHelmetAsync
import Header from './Header'
import PublicFooter from '../public/PublicFooter'
import HeroSection from './HeroSection'
import BeforeAfterSection from './BeforeAfterSection'
import ComoFuncionaSection from './ComoFuncionaSection'
import BankPreviewSection from './BankPreviewSection'
import BeneficiosSection from './BeneficiosSection'
import EditorPreviewSection from './EditorPreviewSection'
import PricingSection from './PricingSection'
import FaqSection from './FaqSection'
import CTAFinalSection from './CTAFinalSection'
import { useTheme } from '../../contexts/ThemeContext'

type Audience = 'b2c' | 'b2b'

const scrollToHashSection = () => {
  if (window.location.hash) {
    const id = window.location.hash.replace('#', '')
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const LandingPage: React.FC = () => {
  const [audience, setAudience] = useState<Audience>('b2c')
  const { theme } = useTheme()

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark')
    } else if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      if (prefersDark) document.documentElement.classList.add('dark')
      else document.documentElement.classList.remove('dark')
    }
  }, [theme])

  // Audiência só via URL/UTM (tráfego pago): personaliza pricing + CTA final.
  // Sem toggle visível na página, não persistimos mais em localStorage.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get('audience')
    const fromUtm = params.get('utm_audience')
    if (fromUrl === 'b2b' || fromUrl === 'b2c') setAudience(fromUrl)
    else if (fromUtm === 'b2b') setAudience('b2b')
  }, [])

  useEffect(() => {
    scrollToHashSection()
    const onHashChange = () => scrollToHashSection()
    window.addEventListener('hashchange', onHashChange)
    document.title = 'Atividade Pronta'
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const finalCta = audience === 'b2b'
    ? {
        text: 'Falar com nosso time',
        href: 'https://wa.me/5511999999999',
        external: true,
      }
    : {
        text: 'Começar grátis',
        href: '/register',
        external: false,
      }

  return (
    <div className="min-h-screen bg-cream-100 dark:bg-ink-900">
      <Helmet>
        <title>Atividade Pronta | Crie avaliações em minutos</title>
        <meta name="description" content="Plataforma para professores criarem avaliações profissionais a partir de um banco curado de questões alinhado à BNCC, com templates e geração de PDF otimizado." />
        <meta property="og:title" content="Atividade Pronta | Crie avaliações em minutos" />
        <meta property="og:description" content="Plataforma para professores criarem avaliações profissionais a partir de um banco curado de questões alinhado à BNCC." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://atividadepronta.com.br/" />
        <meta property="og:image" content="https://atividadepronta.com.br/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Atividade Pronta | Crie avaliações em minutos" />
        <meta name="twitter:description" content="Plataforma para professores criarem avaliações profissionais a partir de um banco curado de questões alinhado à BNCC." />
        <meta name="twitter:image" content="https://atividadepronta.com.br/og-image.png" />
        <link rel="canonical" href="https://atividadepronta.com.br/" />
      </Helmet>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:bg-terra-500 focus:text-cream-50 focus:px-4 focus:py-2 focus:rounded-caderno-xs focus:z-50"
      >
        Pular para o conteúdo
      </a>

      <Header />

      <main id="main-content">
        <HeroSection />

        <BeforeAfterSection />
        <ComoFuncionaSection />
        <BankPreviewSection />
        <BeneficiosSection />
        <EditorPreviewSection />
        <PricingSection highlight={audience === 'b2b' ? 'escolar' : 'premium'} />
        <FaqSection />
        <CTAFinalSection
          ctaText={finalCta.text}
          ctaHref={finalCta.href}
          external={finalCta.external}
        />
      </main>

      <PublicFooter />
    </div>
  )
}

export default LandingPage
